import {
	Box,
	Flex,
	Image,
	Text,
	VStack,
	Divider,
} from '@chakra-ui/react';
import SparklesIcon from 'src/assets/svgs/sparklesIcon.svg';
import FusionCopilot from 'src/fusion-copilot/components';
import {
	RIGHT_SIDEBAR_WIDTH,
	RIGHT_SIDEBAR_WIDTH_MOBILE,
	RIGHT_SIDEBAR_WIDTH_COLLAPSED,
	TOP_BAR_HEIGHT,
} from 'src/components/campaigns/utils/constants';
import { AccordionTab } from './AccordionTab';
import { PanelConfig, SidebarPanel } from './useRightSidebar';
import React, { ComponentType } from 'react';
import { useRealtimeChatContext } from 'src/fusion-copilot/hooks/useRealtimeChatContext';
import { useLocation } from 'react-router-dom';
import withAppInputConfigProvider from 'src/contexts/app/withAppConfigProvider';
import { BiSupport } from 'react-icons/bi';
import ContactForm from 'src/components/common/ContactForm';

interface RightSidebarProps {
	isOpen: boolean;
	activePanel: SidebarPanel;
	onPanelToggle: (panel: SidebarPanel) => void;
	onClose: () => void;
	isScreenBlocked: boolean;
	onOpenSidebar: (panel: SidebarPanel) => void;
	panels?: PanelConfig[];
}

const DEFAULT_PANELS: PanelConfig[] = [
	{
		id: 'copilot',
		icon: SparklesIcon,
		title: 'Fusion Copilot',
		color: 'orange.base',
		content: FusionCopilot,
		order: 1,
	},
	{
		id: 'support',
		icon: BiSupport,
		title: 'Support',
		color: 'inherit',
		content: ContactForm,
		order: 2,
	},
];

const SUPPORT_PANEL_ID = 'support';

/**
 * RightSidebar Component
 *
 * IMPORTANT - Desktop/Mobile Header Distinction:
 * ----------------------------------------------
 * This component uses DIFFERENT headers on desktop vs mobile:
 *
 * DESKTOP (md breakpoint and above):
 *   - Uses AccordionTab as the header for each panel
 *   - AccordionTab renders the "Start new chat" button for Fusion Copilot
 *   - See: src/layout/main/right-sidebar/AccordionTab.tsx
 *
 * MOBILE (below md breakpoint):
 *   - Uses a dedicated Header component inside FusionCopilot
 *   - See: src/fusion-copilot/components/header.tsx
 *   - Display logic: src/fusion-copilot/components/index.tsx
 *     <Box display={{ base: 'block', md: 'none' }}>
 *
 * Both use the shared NewChatButton component for consistency.
 */
const RightSidebarComponent = ({
	isOpen,
	activePanel,
	onPanelToggle,
	onClose,
	onOpenSidebar,
	panels = DEFAULT_PANELS,
}: RightSidebarProps) => {
	const { openCopilot, closeCopilot, isScreenBlocked } =
		useRealtimeChatContext();
	const location = useLocation();
	const isHomePage = location.pathname === '/home';

	// Use screen.width instead of window.innerWidth to detect actual device size
	// This avoids issues with viewport manipulation
	const checkIfMobile = () => typeof window !== 'undefined' && window.screen.width < 768;

	const [isMobileDevice, setIsMobileDevice] = React.useState(checkIfMobile());
	const sidebarWidth = isMobileDevice
		? RIGHT_SIDEBAR_WIDTH_MOBILE
		: RIGHT_SIDEBAR_WIDTH;

	// Refs to manage state
	const emailVerificationProcessedRef = React.useRef(false);
	const screenBlockingUsedRef = React.useRef(false);

	React.useEffect(() => {
		const handleResize = () => {
			setIsMobileDevice(checkIfMobile());
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Check if this is an email verification redirect
	const isFromEmailVerification = React.useMemo(() => {
		return (
			sessionStorage.getItem('copilotState') !== null &&
			!emailVerificationProcessedRef.current
		);
	}, []);

	// Show all panels on all pages (no filtering by page)
	const filteredPanels = React.useMemo(() => {
		return panels;
	}, [panels]);

	const sortedPanels = [...filteredPanels].sort(
		(a, b) => (a.order ?? 0) - (b.order ?? 0),
	);

	// Custom close handler
	const handleClose = React.useCallback(() => {
		// Close copilot if active
		if (activePanel === 'copilot') {
			closeCopilot();
		}

		// Close the sidebar
		onClose();

		// Clean up any remaining state
		if (sessionStorage.getItem('copilotState')) {
			sessionStorage.removeItem('copilotState');
		}
	}, [activePanel, closeCopilot, onClose]);

	// Handle email verification auto-open (only once per session)
	React.useEffect(() => {
		if (
			isFromEmailVerification &&
			isHomePage &&
			!emailVerificationProcessedRef.current
		) {
			emailVerificationProcessedRef.current = true;
			screenBlockingUsedRef.current = true;

			// Open sidebar with copilot
			onOpenSidebar('copilot');

			// Set up screen blocking after a small delay
			setTimeout(() => {
				openCopilot(true);
				// Clean up sessionStorage after setting up blocking
				sessionStorage.removeItem('copilotState');
			}, 100);
		}
	}, [isFromEmailVerification, isHomePage, onOpenSidebar, openCopilot]);

	// Handle normal copilot opening (when user manually opens it)
	React.useEffect(() => {
		// Only open copilot normally if:
		// 1. Sidebar is open and copilot is active
		// 2. We're not in the middle of email verification process
		// 3. Screen blocking hasn't been used in this session
		if (
			isOpen &&
			activePanel === 'copilot' &&
			emailVerificationProcessedRef.current &&
			!screenBlockingUsedRef.current
		) {
			openCopilot();
		}
	}, [isOpen, activePanel, openCopilot]);

	// Keep copilot open across all pages
	// No need to close it when navigating away from home

	const renderPanelContent = (panel: SidebarPanel) => {
		if (!panel) return null;
		const config = sortedPanels.find((p) => p.id === panel);
		if (!config) return null;
		const Component = config.content;
		return <Component />;
	};

	return (
		<>
			<Box
				position="fixed"
				right={0}
				top={TOP_BAR_HEIGHT}
				w={sidebarWidth}
				h={`calc(100vh - ${TOP_BAR_HEIGHT})`}
				zIndex={9999}
				transform={isOpen ? 'translateX(0)' : 'translateX(100%)'}
				transition="transform 0.3s ease-in-out"
			>
				<Flex
					w="full"
					h="full"
					bg="white"
					boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
					overflow="hidden"
					position="relative"
					direction="column"
				>
					<Flex direction="column" flex="1" minH={0}>
						{sortedPanels.map((config, idx) => (
							<Flex
								key={config.id}
								direction="column"
								flex={activePanel === config.id ? '1' : '0 0 auto'}
								minH={0}
							>
								{/* Hide AccordionTab on mobile for copilot since we have sticky header */}
								<Box
									display={{
										base: config.id === 'copilot' ? 'none' : 'block',
										md: 'block',
									}}
								>
									<AccordionTab
										icon={config.icon}
										title={config.title}
										isExpanded={activePanel === config.id}
										onClick={() => onPanelToggle(config.id)}
										color={config.color}
										onClose={handleClose}
										showNewChatButton={config.id === 'copilot'}
									/>
								</Box>
								{activePanel === config.id && (
									<Box
										flex="1"
										minH={0}
										overflow="hidden"
										borderBottom="1px solid #eee"
										position="relative"
										h={{
											base:
												config.id === 'copilot'
													? `calc(100vh - ${TOP_BAR_HEIGHT})` // Full height minus TopBar for copilot on mobile
													: `calc(100vh - ${TOP_BAR_HEIGHT} - 48px)`, // Other panels keep accordion tab
											md: `calc(100vh - ${TOP_BAR_HEIGHT} - 48px)`,
										}}
										bg="white"
										sx={{
											'& > *': {
												width: '100%',
												height: '100%',
											},
										}}
									>
										{renderPanelContent(config.id)}
									</Box>
								)}
								{idx < sortedPanels.length - 1 && <Divider />}
							</Flex>
						))}
					</Flex>
				</Flex>
			</Box>

			{!isOpen && (
				<Box
					position="fixed"
					right={0}
					top={TOP_BAR_HEIGHT}
					w={RIGHT_SIDEBAR_WIDTH_COLLAPSED}
					h={`calc(100vh - ${TOP_BAR_HEIGHT})`}
					bg="white"
					boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
					zIndex={9999}
					display="flex"
					alignItems="start"
					justifyContent="center"
					py="20px"
				>
					<VStack spacing={4}>
						{sortedPanels.map((config) => (
							<VStack
								key={config.id}
								spacing={1}
								cursor="pointer"
								onClick={() => onOpenSidebar(config.id)}
								_hover={{ opacity: 0.7 }}
								transition="opacity 0.2s"
							>
								<Box
									w={7}
									h={7}
									display="flex"
									alignItems="center"
									justifyContent="center"
									position="relative"
								>
									{typeof config.icon === 'string' ? (
										<Image
											src={config.icon}
											w={7}
											pointerEvents="none"
										/>
									) : (
										config.icon &&
										React.createElement(config.icon as ComponentType<any>, {
											style: {
												fontSize: 26,
												color: config.color || 'gray.800',
												pointerEvents: 'none'
											},
										})
									)}
								</Box>
								<Text
									fontSize="10px"
									fontWeight="600"
									color={config.color || 'gray.800'}
									textAlign="center"
									lineHeight="1.2"
									maxW="70px"
									noOfLines={2}
									wordBreak="break-word"
								>
									{config.title}
								</Text>
							</VStack>
						))}
					</VStack>
				</Box>
			)}

			{isOpen &&
				activePanel === 'copilot' &&
				isScreenBlocked &&
				!isMobileDevice && (
					<Box
						position="fixed"
						top={TOP_BAR_HEIGHT}
						right={isMobileDevice ? '0' : sidebarWidth}
						left={0}
						h={`calc(100vh - ${TOP_BAR_HEIGHT})`}
						w={isMobileDevice ? '0' : `calc(100vw - ${sidebarWidth})`}
						bg="black"
						opacity={0.9}
						p={10}
						textAlign="center"
						zIndex={1700}
						display={isMobileDevice ? 'none' : 'flex'}
						justifyContent="center"
						alignItems="center"
					>
						<Text
							fontSize="xl"
							fontWeight="bold"
							color="white"
							w="80%"
							lineHeight="1.5"
						>
							Welcome to FusionAds.ai! Our copilot will help you to setup your
							account and launch your first ad.
						</Text>
					</Box>
				)}
		</>
	);
};

export const RightSidebar = withAppInputConfigProvider(RightSidebarComponent);
